import { z } from 'zod';

// ISO 4217 alpha-3 codes — normalised to uppercase on input so the service can
// do case-sensitive equality without surprises. Legacy stored whatever case
// callers passed.
const currencyCode = z
  .string()
  .regex(/^[A-Za-z]{3}$/, 'must be a 3-letter ISO 4217 code')
  .transform((v) => v.toUpperCase());

// Rate stored as numeric — Drizzle returns string. Expose as string on read,
// accept number-or-string on write to avoid float drift, require positive.
const rateValue = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toString() : v))
  .refine((v) => /^\d+(\.\d+)?$/.test(v) && Number.parseFloat(v) > 0, {
    message: 'rate must be a positive decimal',
  });

// id is z.string() (not z.uuid()) so the synthetic self-pair sentinel
// `${currency}-self` injected by findByTargetCurrency/findBySourceCurrency
// validates alongside real persisted rows.
export const exchangeRateItemSchema = z.object({
  id: z.string(),
  sourceCurrency: z.string(),
  targetCurrency: z.string(),
  exchangeRate: z.string().nullable(),
  createdOn: z.string().nullable(),
  modifiedOn: z.string().nullable(),
});

export type ExchangeRateItem = z.infer<typeof exchangeRateItemSchema>;

export const findAllExchangeRatesResultSchema = z.object({
  data: z.array(exchangeRateItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindAllExchangeRatesResult = z.infer<
  typeof findAllExchangeRatesResultSchema
>;

export const findByCurrencyInputSchema = z.object({
  currency: currencyCode,
});

export type FindByCurrencyInput = z.infer<typeof findByCurrencyInputSchema>;

export const findByDateInputSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
});

export type FindByDateInput = z.infer<typeof findByDateInputSchema>;

export const getRateInputSchema = z.object({
  from: currencyCode,
  to: currencyCode,
});

export type GetRateInput = z.infer<typeof getRateInputSchema>;

export const getRateResultSchema = z.object({
  from: z.string(),
  to: z.string(),
  rate: z.string(),
});

export type GetRateResult = z.infer<typeof getRateResultSchema>;

export const createExchangeRateInput = z.object({
  sourceCurrency: currencyCode,
  targetCurrency: currencyCode,
  exchangeRate: rateValue,
});

export type CreateExchangeRateInput = z.infer<typeof createExchangeRateInput>;

export const updateExchangeRateInput = z.object({
  id: z.uuid(),
  exchangeRate: rateValue,
});

export type UpdateExchangeRateInput = z.infer<typeof updateExchangeRateInput>;

export const bulkUpdateExchangeRatesInput = z.object({
  rates: z.array(updateExchangeRateInput).min(1),
});

export type BulkUpdateExchangeRatesInput = z.infer<
  typeof bulkUpdateExchangeRatesInput
>;

export const bulkUpdateExchangeRatesResultSchema = z.object({
  data: z.array(exchangeRateItemSchema),
});

export type BulkUpdateExchangeRatesResult = z.infer<
  typeof bulkUpdateExchangeRatesResultSchema
>;

export const deleteExchangeRateInput = z.object({
  id: z.uuid(),
});

export type DeleteExchangeRateInput = z.infer<typeof deleteExchangeRateInput>;

export const deleteExchangeRateResultSchema = z.object({
  id: z.uuid(),
});

export type DeleteExchangeRateResult = z.infer<
  typeof deleteExchangeRateResultSchema
>;

export const findDistinctCurrenciesResultSchema = z.object({
  currencies: z.array(z.string()),
});

export type FindDistinctCurrenciesResult = z.infer<
  typeof findDistinctCurrenciesResultSchema
>;

export const triggerSyncResultSchema = z.object({
  status: z.enum(['queued', 'completed', 'not-implemented']),
  ratesUpdated: z.number().int().nonnegative().optional(),
  message: z.string().optional(),
});

export type TriggerSyncResult = z.infer<typeof triggerSyncResultSchema>;
